import React from 'react';
import {
  Card, CardContent, Typography, Box, Chip, Divider, Stack, Checkbox, Button, CircularProgress
} from '@mui/material';

import { nomeDoIndicador } from '../diagnostico/catalogo';
import { rotuloDoPrazo, resumoDoPlano } from '../telas/planoDeAcaoApresentacao';

const sections = [
  { key: 'h30', title: 'Próximos 30 dias', color: 'error' },
  { key: 'h60', title: 'Próximos 60 dias', color: 'warning' },
  { key: 'h90', title: 'Próximos 90 dias', color: 'info' }
];

/** Chip cujo rótulo é informação: quebra em várias linhas em vez de truncar. */
const CHIP_QUE_QUEBRA = {
  height: 'auto',
  maxWidth: '100%',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    display: 'block',
    py: 0.5,
    lineHeight: 1.4
  }
};

/** A cor do prazo é a mesma régua do servidor, traduzida uma vez só. */
const COR_DO_PRAZO = {
  vencida: 'warning',
  urgente: 'error',
  adiada: 'default',
  feita: 'success',
  normal: 'default'
};

/**
 * O plano 30-60-90 — agora com estado.
 *
 * O QUE MUDOU E POR QUÊ. Até esta frente o plano era write-only: recalculado do
 * zero a cada requisição e nunca lido de volta. A pessoa executava duas
 * iniciativas, voltava no mês seguinte e via a mesma lista intacta, como se
 * nada tivesse acontecido. Marcar como feita agora tem onde se apoiar, e o
 * prazo conta a partir da primeira vez que a iniciativa apareceu para ela — não
 * a partir do último recálculo, que renovaria o prazo a cada recarregamento.
 *
 * OS CONTROLES SÓ EXISTEM ONDE HÁ ONDE GUARDAR. Visitante sem conta e conta sem
 * mês gravado continuam vendo o plano inteiro, como leitura: é o tier gratuito
 * e ele não encolhe. O que eles não têm é `chave` — a identidade da iniciativa
 * — e sem ela um caixa de seleção seria um botão que esquece.
 */
const ActionPlan = ({ plan, aoMarcar, salvando = null }) => {
  if (!plan) return null;

  const hasTasks = sections.some(section => Array.isArray(plan[section.key]) && plan[section.key].length > 0);

  if (!hasTasks) return null;

  const resumo = resumoDoPlano(plan);
  const interativo = resumo.acompanhado && typeof aoMarcar === 'function';

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 0.5 }}
        >
          <Typography variant="h6">Plano de Ação 30-60-90 dias</Typography>
          {resumo.acompanhado && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {/* "1 de 2 feita" e "1 de 2 feitas" erram os dois: o adjetivo
                  concorda com a contagem, mas quem está no fim da frase é o
                  total. Rótulo nomeado resolve sem malabarismo gramatical — e
                  plural quebrado num documento de consultoria é o detalhe que
                  faz o leitor duvidar do resto da página. */}
              <Chip
                size="small"
                variant="outlined"
                label={`Feitas: ${resumo.feitas} de ${resumo.total}`}
              />
              {resumo.vencidas > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${resumo.vencidas} ${resumo.vencidas === 1 ? 'vencida' : 'vencidas'}`}
                />
              )}
            </Stack>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {interativo
            ? 'Marque o que já foi feito e adie o que não vai caber no prazo. O que você marcar continua '
              + 'marcado na próxima visita, mesmo depois de os números serem recalculados.'
            : 'Priorize as ações críticas nas próximas semanas para estabilizar liquidez e margens, '
              + 'mantendo um pipeline de melhorias contínuas.'}
        </Typography>

        {sections.map((section, index) => {
          const tasks = Array.isArray(plan[section.key]) ? plan[section.key] : [];

          return (
            <Box key={section.key} sx={{ mb: index < sections.length - 1 ? 2 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {/* Horizonte sem ação não usa a cor de urgência: vermelho ao lado
                    de "Sem ações priorizadas" lê como alarme, quando a notícia é boa. */}
                <Chip
                  label={section.title}
                  color={tasks.length > 0 ? section.color : 'default'}
                  variant={tasks.length > 0 ? 'filled' : 'outlined'}
                  size="small"
                />
                <Typography variant="subtitle2" color="text.secondary">
                  {/* "1 iniciativas" ficou na tela ao lado do Chip que este
                      commit ajustou. Plural quebrado num documento de
                      consultoria é a trinca que faz o leitor duvidar do resto. */}
                  {tasks.length > 0
                    ? `${tasks.length} ${tasks.length === 1 ? 'iniciativa' : 'iniciativas'}`
                    : 'Sem ações priorizadas'}
                </Typography>
              </Box>

              {tasks.length > 0 && (
                <Stack spacing={1.5}>
                  {tasks.map((task, taskIndex) => {
                    const feita = task.estado === 'feita';
                    const prazo = rotuloDoPrazo(task);
                    const emCurso = salvando === task.chave;
                    const podeMarcar = interativo && Boolean(task.chave);

                    return (
                      <Box
                        key={`${section.key}-${task.chave || task.insightId}-${taskIndex}`}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: task.vencida ? 'warning.main' : 'divider',
                          p: 1.5,
                          bgcolor: 'background.paper',
                          // Feita continua legível, e some do primeiro plano: é
                          // registro do que foi resolvido, não trabalho pendente.
                          opacity: feita ? 0.62 : 1,
                          transition: 'opacity 180ms ease, border-color 180ms ease'
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          {podeMarcar && (
                            <Checkbox
                              checked={feita}
                              disabled={emCurso}
                              onChange={() => aoMarcar(task.chave, feita ? 'aberta' : 'feita')}
                              size="small"
                              sx={{ mt: -0.5, ml: -1 }}
                              inputProps={{
                                'aria-label': feita
                                  ? `Desmarcar: ${task.action || task.focus}`
                                  : `Marcar como feita: ${task.action || task.focus}`
                              }}
                            />
                          )}
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                textDecoration: feita ? 'line-through' : 'none'
                              }}
                            >
                              {task.action || task.focus}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                              {task.focus}
                            </Typography>
                            {/* CHIP QUE CORTA O NOME DO INDICADOR NÃO INFORMA NADA.
                                O Chip do MUI trunca o rótulo com reticências numa
                                linha só. No celular isso virava "Acompanhar: Taxas de
                                meio de pagamento / Rec" — o plano de ação mandava
                                acompanhar um indicador cujo nome a pessoa não
                                conseguia ler. Aqui o rótulo é o conteúdo, não
                                decoração: ele quebra em mais linhas e o chip cresce. */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                              {task.owner && (
                                <Chip
                                  label={`Responsável: ${task.owner}`}
                                  size="small"
                                  variant="outlined"
                                  sx={CHIP_QUE_QUEBRA}
                                />
                              )}
                              {task.kpi && task.kpi.length > 0 && (
                                <Chip
                                  label={`Acompanhar: ${task.kpi.map(nomeDoIndicador).join(', ')}`}
                                  size="small"
                                  variant="outlined"
                                  sx={CHIP_QUE_QUEBRA}
                                />
                              )}
                              {/* O PRAZO SUBSTITUI O RÓTULO DO HORIZONTE QUANDO EXISTE.
                                  "90 dias" ao lado de uma tarefa que a pessoa vê há
                                  quatro meses é um prazo que nunca vence. Com data
                                  gravada, o chip diz quanto falta — ou quanto passou. */}
                              <Chip
                                label={prazo ? prazo.texto : (task.deadline || section.title)}
                                size="small"
                                color={prazo ? COR_DO_PRAZO[prazo.tom] : section.color}
                                variant="outlined"
                                sx={CHIP_QUE_QUEBRA}
                              />
                              {podeMarcar && !feita && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    aoMarcar(task.chave, task.estado === 'adiada' ? 'aberta' : 'adiada')}
                                  disabled={emCurso}
                                  startIcon={emCurso ? <CircularProgress size={14} color="inherit" /> : null}
                                  sx={{ minHeight: 36 }}
                                >
                                  {task.estado === 'adiada' ? 'Voltar ao prazo' : 'Adiar 30 dias'}
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              )}

              {index < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default ActionPlan;
